import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  BarChart3,
  Building2,
  Clock,
  DollarSign,
  LineChart,
  Percent,
  Target,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import { executiveDashboardService } from '../../services/executiveDashboardService';
import { financeViewService } from '../../services/financeViewService';
import { DollarsAtRiskPanel, AgingComposePanel, FinanceDeepDivePanel } from './FinanceComposePanels';
import { buildFinanceDeepDive } from '@saas-foundation/shared-frontend/financeView';
import { useEffectiveAccess } from '../../agenticsaas/contexts/EffectiveAccessContext';
import {
  normalizeReportingPeriod,
  reportingPeriodKey,
} from '../../utils/reportingPeriod';
import { useDashboardReportingPeriod } from './hooks/useDashboardReportingPeriod';
import DashboardCommandHeader from './components/DashboardCommandHeader';
import { useDashboardBranding } from './hooks/useDashboardBranding';

const SECTION_PANEL = 'bg-white rounded-lg shadow-sm border border-gray-200';
const KPI_CARD =
  'bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow border-l-4';
const KPI_CARD_INACTIVE = 'bg-gray-50 p-6 rounded-lg shadow-sm border border-dashed border-gray-300 border-l-4 border-l-gray-300';

const KPI_ICONS = {
  net_collection_rate: Percent,
  days_in_ar: Clock,
  denial_rate: AlertTriangle,
  observed_capture: Target,
  ehr_coverage: Building2,
  remit_coverage: Percent,
  net_revenue: DollarSign,
};

const FINANCE_KPI_IDS = new Set([
  'net_collection_rate',
  'days_in_ar',
  'denial_rate',
  'observed_capture',
  'ehr_coverage',
  'remit_coverage',
]);

function formatCurrency(value) {
  if (value == null || Number.isNaN(value)) return '—';
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${Math.round(value / 1_000)}K`;
  return `$${Math.round(value).toLocaleString()}`;
}

function TrendChip({ delta, polarity, unit }) {
  if (delta == null) return null;
  const improved =
    polarity === 'lower_is_better' ? delta < 0 : polarity === 'higher_is_better' ? delta > 0 : delta > 0;
  const Icon = improved ? TrendingUp : TrendingDown;
  const color = improved ? 'text-emerald-600' : 'text-red-600';
  const suffix = unit === 'percent' ? '%' : unit === 'days' ? 'd' : '';
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${color}`}>
      <Icon className="w-3.5 h-3.5" />
      {delta > 0 ? '+' : ''}
      {delta}
      {suffix} vs prior
    </span>
  );
}

function TopKpiTile({ kpi, onOpen }) {
  const Icon = KPI_ICONS[kpi.id] || (kpi.custom ? LineChart : BarChart3);
  const iconColor = kpi.custom ? 'text-indigo-600' : 'text-blue-600';
  const clickable = Boolean(onOpen);

  if (!kpi.live) {
    return (
      <div className={KPI_CARD_INACTIVE}>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-gray-600">{kpi.label}</h3>
          <Icon className="w-5 h-5 text-gray-400" />
        </div>
        <p className="text-sm text-gray-500 mt-2">{kpi.integrationLabel || 'Connect a data source to populate this metric'}</p>
        <span className="inline-block mt-3 text-xs font-medium px-2 py-1 rounded bg-amber-100 text-amber-800">
          Integration required
        </span>
      </div>
    );
  }

  const display =
    kpi.unit === 'percent'
      ? `${Number(kpi.value).toFixed(1)}%`
      : kpi.unit === 'days'
        ? `${Number(kpi.value).toFixed(1)}`
        : kpi.unit === 'currency'
          ? formatCurrency(kpi.value)
          : Number(kpi.value).toLocaleString();

  return (
    <div
      className={`${KPI_CARD} border-l-blue-500 ${clickable ? 'cursor-pointer' : ''}`}
      onClick={onOpen}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter') onOpen(); } : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
    >
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium text-gray-600">{kpi.label}</h3>
        <Icon className={`w-5 h-5 ${iconColor}`} />
      </div>
          <p className="text-3xl font-bold text-gray-900">{kpi.value == null || Number.isNaN(Number(kpi.value)) ? '—' : display}</p>
      {kpi.caption ? <p className="text-xs text-gray-500 mt-1">{kpi.caption}</p> : null}
      <div className="mt-2 flex items-center justify-between gap-2">
        <TrendChip delta={kpi.delta} polarity={kpi.polarity} unit={kpi.unit} />
        {clickable ? <span className="text-xs font-medium text-blue-700">View details →</span> : null}
      </div>
    </div>
  );
}

function SectionPanel({ icon: Icon, title, subtitle, children, action }) {
  return (
    <section className={SECTION_PANEL}>
      <div className="px-6 py-4 border-b border-gray-100 flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          {Icon ? <Icon className="w-5 h-5 text-blue-600 mt-0.5" /> : null}
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            {subtitle ? <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p> : null}
          </div>
        </div>
        {action}
      </div>
      <div className="p-6">{children}</div>
    </section>
  );
}

/**
 * Finance View — platform financial KPIs (separate from Executive / Operational).
 */
export default function FinanceDashboard() {
  const navigate = useNavigate();
  const { canAccessNavItem } = useEffectiveAccess();
  const { branding, organizationId, user, resolvedOrgName } = useDashboardBranding({ mode: 'finance' });
  const { reportingPeriod, onPeriodChange } = useDashboardReportingPeriod();
  const deepDive = useMemo(() => buildFinanceDeepDive(canAccessNavItem), [canAccessNavItem]);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['finance-dashboard', 'native-only', reportingPeriodKey(reportingPeriod), organizationId, resolvedOrgName],
    queryFn: () =>
      executiveDashboardService.getExecutiveDashboardData({
        period: normalizeReportingPeriod(reportingPeriod),
        organizationName: resolvedOrgName,
        organizationId,
        includeCustomMetrics: false,
      }),
    enabled: Boolean(organizationId),
    staleTime: 120000,
  });

  const { data: atRisk } = useQuery({
    queryKey: ['finance-at-risk', organizationId],
    queryFn: () => financeViewService.getDollarsAtRisk(organizationId),
    enabled: Boolean(organizationId),
    staleTime: 120000,
  });

  const aging = useMemo(
    () => financeViewService.composeAging({ daysInArKpi: data?.daysInArDetail }),
    [data]
  );

  const financeKpis = useMemo(
    () => (data?.topKpis || []).filter((kpi) => FINANCE_KPI_IDS.has(kpi.id) && !kpi.custom),
    [data?.topKpis]
  );

  const financeBranding = useMemo(
    () => ({
      title: branding?.title || resolvedOrgName || 'Finance View',
      subtitle: branding?.subtitle || 'Platform financial KPIs from our own books',
    }),
    [branding, resolvedOrgName]
  );

  return (
    <div className="flex flex-col gap-6">
      <DashboardCommandHeader
        mode="finance"
        branding={financeBranding}
        period={reportingPeriod}
        onPeriodChange={onPeriodChange}
        onRefresh={() => refetch()}
        isFetching={isFetching}
        user={user}
        refreshedAt={data?.refreshedAt}
        statusMessage={data?.dataStatus === 'partial' ? 'Some finance sources unavailable' : undefined}
      />

      {isLoading && (
        <div className={`${SECTION_PANEL} p-8 text-center text-gray-500`}>Loading Finance View…</div>
      )}

      {!isLoading && data && (
        <>
          <SectionPanel
            icon={BarChart3}
            title="Finance View KPIs"
            subtitle="NCR, denials, days in A/R, and capture rates from our platform"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {financeKpis.map((kpi) => (
                <TopKpiTile
                  key={kpi.id}
                  kpi={kpi}
                  onOpen={
                    deepDive.kpiPath[kpi.id]
                      ? () => navigate(deepDive.kpiPath[kpi.id])
                      : undefined
                  }
                />
              ))}
            </div>
          </SectionPanel>

          <FinanceDeepDivePanel
            links={deepDive.links}
            onOpen={(path) => navigate(path)}
          />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <DollarsAtRiskPanel atRisk={atRisk} />
            <AgingComposePanel aging={aging} />
          </div>
        </>
      )}
    </div>
  );
}
